import { ref, computed, watch } from "vue";
import { watchDebounced } from "@vueuse/core";
import { useAppToast } from "@/shared/composables/useAppToast";
import { logger } from "@/infrastructure/services/LoggerService";
import { getListViews, saveListViews, } from "../infrastructure/adapters/ListViewPreferencesService";
const SAVE_DEBOUNCE_MS = 500;
const SCHEMA_VERSION = 1;
const SCOPE_ORDER = ["eudonet", "admin", "myLists", "shared"];
export function useListViewPreferences(options) {
    const { entity, tableQuery, tableViews, allLists, defaultPinnedIds, hasUrlView, labels } = options;
    const { toastError } = useAppToast();
    const pinnedListIds = ref([]);
    let restored = false;
    let lastSavedJson = "";
    /** The id of the locked default list (isDefault), kept first and non-removable. */
    const defaultListId = computed(() => allLists.value.find((l) => l.isDefault)?.id ?? null);
    function listById(id) {
        return allLists.value.find((l) => l.id === id);
    }
    function syncViews() {
        // Remove views no longer pinned.
        for (const v of [...tableViews.views.value]) {
            if (!pinnedListIds.value.includes(v.id))
                tableViews.removeView(v.id);
        }
        // Add newly pinned lists (resolved to a label).
        for (const id of pinnedListIds.value) {
            const entry = listById(id);
            if (!entry)
                continue;
            if (!tableViews.views.value.find((v) => v.id === id)) {
                tableViews.addView({ id, label: entry.label, isPreset: true, querySnapshot: {} });
            }
            else {
                tableViews.updateView(id, { label: entry.label });
            }
        }
        tableViews.reorderViews(pinnedListIds.value);
    }
    function currentPayload() {
        return {
            version: SCHEMA_VERSION,
            entity,
            pinnedListIds: [...pinnedListIds.value],
            activeViewId: tableQuery.state.activeViewId ?? null,
        };
    }
    async function persist() {
        const payload = currentPayload();
        const json = JSON.stringify(payload);
        if (json === lastSavedJson)
            return;
        try {
            await saveListViews(payload);
            lastSavedJson = json;
        }
        catch (error) {
            logger.error("Failed to save list view preferences", error);
            toastError("table.tabs.saveError");
        }
    }
    /**
     * Normalise an id list: keep only resolvable ids, dedupe, force the default list first.
     */
    function normalise(ids) {
        const seen = new Set();
        const out = [];
        const def = defaultListId.value;
        if (def && listById(def)) {
            out.push(def);
            seen.add(def);
        }
        for (const id of ids) {
            if (seen.has(id) || !listById(id))
                continue;
            seen.add(id);
            out.push(id);
        }
        return out;
    }
    /**
     * Loads persisted pinned list ids and registers them as preset views. Unless the URL
     * already carries a `?view=`, activates the previously active view. Safe to call once
     * on mount.
     */
    async function restore() {
        try {
            const prefs = await getListViews(entity);
            const sourceIds = prefs?.pinnedListIds ?? defaultPinnedIds.value;
            pinnedListIds.value = normalise(sourceIds);
            syncViews();
            const canActivate = !hasUrlView() &&
                !!prefs?.activeViewId &&
                pinnedListIds.value.includes(prefs.activeViewId);
            tableQuery.setView(canActivate ? prefs.activeViewId : defaultListId.value);
        }
        catch (error) {
            logger.error("Failed to load list view preferences", error);
            pinnedListIds.value = normalise(defaultPinnedIds.value);
            syncViews();
            tableQuery.setView(defaultListId.value);
        }
        finally {
            restored = true;
            lastSavedJson = JSON.stringify(currentPayload());
        }
    }
    function togglePin(id) {
        if (id === defaultListId.value)
            return; // locked
        if (!listById(id))
            return;
        if (pinnedListIds.value.includes(id)) {
            pinnedListIds.value = pinnedListIds.value.filter((x) => x !== id);
            if (tableQuery.state.activeViewId === id)
                tableQuery.setView(defaultListId.value);
        }
        else {
            pinnedListIds.value = [...pinnedListIds.value, id];
        }
        syncViews();
    }
    /**
     * Recompute the flat pinned order after the user reorders one scope segment.
     * `orderedSegmentIds` is the new display order for all items (pinned+unpinned) of that
     * segment. We keep the global scope sequence (eudonet → admin → myLists → shared) and within
     * the dragged segment apply the new relative order of its currently pinned ids.
     *
     * Invariant: OPAL's FilterDropdown emits `reorder` with the currently-displayed items
     * for a single active group, so `orderedSegmentIds` is always single-scope. The scope
     * is inferred from the first pinned id in the reordered list.
     *
     * Defensive guard: if pinned ids unexpectedly span more than one scope (should never
     * happen in practice), bail out without reordering to avoid misattributing order.
     */
    function reorder(orderedSegmentIds) {
        const draggedPinned = orderedSegmentIds.filter((id) => pinnedListIds.value.includes(id));
        if (draggedPinned.length === 0)
            return;
        const draggedScope = listById(draggedPinned[0])?.scope;
        // Defensive: if pinned ids span multiple scopes, bail out rather than misattribute.
        if (draggedPinned.some((id) => listById(id)?.scope !== draggedScope))
            return;
        const next = [];
        for (const scope of SCOPE_ORDER) {
            if (scope === draggedScope) {
                next.push(...draggedPinned);
            }
            else {
                next.push(...pinnedListIds.value.filter((id) => listById(id)?.scope === scope));
            }
        }
        pinnedListIds.value = normalise(next);
        syncViews();
    }
    const tabDropdowns = computed(() => {
        const lists = allLists.value;
        const counts = {
            eudonet: lists.filter((l) => l.scope === "eudonet").length,
            admin: lists.filter((l) => l.scope === "admin").length,
            myLists: lists.filter((l) => l.scope === "myLists").length,
            shared: lists.filter((l) => l.scope === "shared").length,
        };
        // pinned-first ordering within each scope for stable drag display
        const pinnedRank = new Map(pinnedListIds.value.map((id, i) => [id, i]));
        const items = lists
            .slice()
            .sort((a, b) => {
            const pa = pinnedRank.has(a.id) ? pinnedRank.get(a.id) : Number.MAX_SAFE_INTEGER;
            const pb = pinnedRank.has(b.id) ? pinnedRank.get(b.id) : Number.MAX_SAFE_INTEGER;
            return pa - pb || a.displayOrder - b.displayOrder;
        })
            .map((l) => ({
            id: l.id,
            label: l.label,
            group: l.scope,
            switchChecked: pinnedListIds.value.includes(l.id),
            disabled: l.id === defaultListId.value,
            onClick: () => togglePin(l.id),
        }));
        return [
            {
                label: labels.value.preFilteredLists,
                iconLeft: "ri-equalizer-line",
                controlledSelection: true,
                reorderable: true,
                showHeader: true,
                selectedCountSuffix: labels.value.selected,
                showSearch: true,
                searchPlaceholder: labels.value.searchPlaceholder,
                searchClearLabel: labels.value.searchClearLabel,
                size: "l",
                overflowLabel: labels.value.lists,
                overflowInfoTooltip: labels.value.overflowTooltip,
                groupButtons: [
                    { value: "eudonet", label: `${labels.value.eudonet} (${counts.eudonet})` },
                    { value: "admin", label: `${labels.value.admin} (${counts.admin})` },
                    { value: "myLists", label: `${labels.value.myLists} (${counts.myLists})` },
                    { value: "shared", label: `${labels.value.shared} (${counts.shared})` },
                ],
                items,
                emptyLabel: labels.value.noListCreated,
                footerButtons: [{ label: labels.value.createList, variant: "primary", disabled: true }],
            },
        ];
    });
    watchDebounced(() => [JSON.stringify(pinnedListIds.value), tableQuery.state.activeViewId], () => {
        if (!restored)
            return;
        void persist();
    }, { debounce: SAVE_DEBOUNCE_MS });
    // Keep the registered tab-strip labels in sync when `allLists` recomputes — notably on an
    // in-session locale switch, which yields freshly translated labels. Without this the visible
    // tabs keep their stale labels until the next pin/reorder/navigation (the old Contact/
    // OrganisationList `watch(presetViews)` used to relabel them). Re-running syncViews() only
    // updates labels/order here; it never mutates `allLists`, so there is no watch loop.
    watch(allLists, () => {
        if (restored)
            syncViews();
    });
    return { pinnedListIds, tabDropdowns, togglePin, reorder, restore };
}
